import React from 'react';
import {Node, Edge, useReactFlow} from 'reactflow';

import {FlowchartData} from '../layout/algorithms';
import {LayoutSettings} from '../layout/calculateDimensionsForLayout';
import layoutFlowchart from '../layout/layoutFlowchart';
import {getFitViewAnimationDurationMs} from '../ReactFlowConstants';
import BaseControlButton from './BaseControlButton';
import {FIT_VIEW_MAX_ZOOM} from './HorizontalControls';

interface ControlledAutoLayoutButtonProps<N extends Node, E extends Edge> {
  flowchartData: FlowchartData<N, E>;
  customLayoutSettings?: Partial<LayoutSettings<N, E>>;
  setNodesAndEdges: (nodes: Array<N>, edges: Array<E>) => void;
  disabled?: boolean;
  onAutolayoutClick?: () => void;
  onAutolayoutComplete?: (durationSeconds: number) => void;
}

export function ControlledAutoLayoutButton<N extends Node, E extends Edge>(
  props: ControlledAutoLayoutButtonProps<N, E>
): JSX.Element {
  const {flowchartData, customLayoutSettings, disabled} = props;
  const {fitView} = useReactFlow();

  return (
    <BaseControlButton
      icon="grid-two"
      tooltip="Auto layout"
      onClick={async () => {
        props.onAutolayoutClick?.();
        const startTime = performance.now();
        const {nodes, edges} = await layoutFlowchart(flowchartData, customLayoutSettings);
        const durationSeconds = (performance.now() - startTime) / 1000;

        props.setNodesAndEdges(nodes, edges);
        props.onAutolayoutComplete?.(durationSeconds);

        // Defer the fitView to the next tick to ensure that the nodes have been rendered and measured.
        setTimeout(() => {
          fitView({duration: getFitViewAnimationDurationMs(), maxZoom: FIT_VIEW_MAX_ZOOM});
        }, 5);
      }}
      dataTestAttrs={{component: 'ControlledAutoLayoutButton', element: 'root'}}
      disabled={disabled}
    />
  );
}
